import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { log } from '@lib/utils/logger';

interface CriticalDataState {
  points: number | null;
  lastUpdated: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: CriticalDataState = {
  points: null,
  lastUpdated: null,
  isLoading: false,
  error: null
};

// Thunk für das Laden der Punktedaten
export const fetchPoints = createAsyncThunk(
  'criticalData/fetchPoints',
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/data/kunden', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('auth_token') || ''
        },
        body: JSON.stringify({
          where: { id: Number(customerId.replace(/[a-zA-Z]/g, '')) },
          fields: {
            kundenkarten: ['punktestand']
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch points');
      }

      const data = await response.json();
      const customerData = data[0]?.kunden;
      const points = Number(
        customerData?.kundenkarten?.filter(
          (karte: any) => 'punktestand' in karte
        )?.[0]?.punktestand
      ) || 0;

      return points;
    } catch (error) {
      log.error('Error fetching points:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);

export const criticalDataSlice = createSlice({
  name: 'criticalData',
  initialState,
  reducers: {
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    setPoints: (state, action: PayloadAction<number>) => {
      state.points = action.payload;
      state.lastUpdated = new Date().toISOString();
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPoints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPoints.fulfilled, (state, action) => {
        state.points = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchPoints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

// Selektoren
export const selectPoints = (state: RootState) => state.criticalData.points;
export const selectPointsLastUpdated = (state: RootState) => state.criticalData.lastUpdated;
export const selectIsLoadingPoints = (state: RootState) => state.criticalData.isLoading;
export const selectPointsError = (state: RootState) => state.criticalData.error;

export const { resetState, setPoints } = criticalDataSlice.actions;

export default criticalDataSlice.reducer; 